import React, { useState, useEffect, useRef } from 'react';
import { Send, Search, User, CheckCheck, Clock, Sparkles } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function AdminCreatorMessages() {
  const [creators, setCreators] = useState([]);
  const [selectedCreator, setSelectedCreator] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  // Fetch creators with message threads
  useEffect(() => {
    fetchCreators();
  }, []);

  // Fetch messages when creator selected
  useEffect(() => {
    if (selectedCreator) {
      fetchMessages(selectedCreator.id);
    }
  }, [selectedCreator]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchCreators = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/admin/creator-threads`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCreators(data);
      }
    } catch (error) {
      console.error('Error fetching creators:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (creatorId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/admin/messages/${creatorId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMessages(data);
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedCreator) return;

    setSending(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/admin/messages/${selectedCreator.id}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ content: newMessage })
      });

      if (res.ok) {
        const message = await res.json();
        setMessages(prev => [...prev, message]);
        setNewMessage('');
        
        // Update creator's last message
        setCreators(prev => prev.map(c => 
          c.id === selectedCreator.id 
            ? { ...c, last_message: newMessage, last_message_at: new Date().toISOString() }
            : c
        ));
      } else {
        toast.error('Failed to send message');
      }
    } catch (error) {
      toast.error('Error sending message');
    } finally {
      setSending(false);
    }
  };

  const filteredCreators = creators.filter(c =>
    c.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div data-testid="admin-creator-messages">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-teko">Creator Messages</h2>
          <p className="text-sm text-[var(--text-secondary)]">Direct communication with creators</p>
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 bg-[var(--primary)]/10 rounded-full">
          <Sparkles size={14} className="text-[var(--primary)]" />
          <span className="text-sm text-[var(--primary)]">{creators.length} creators</span>
        </div>
      </div>

      <div className="flex gap-6 h-[600px]">
        {/* Creator List */}
        <div className="w-80 flex-shrink-0 bg-[var(--surface)] border border-[var(--border)] rounded-xl overflow-hidden flex flex-col">
          {/* Search */}
          <div className="p-4 border-b border-[var(--border)]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" size={16} />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search creators..."
                className="pl-9 text-sm"
              />
            </div>
          </div>

          {/* Creator List */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center text-[var(--text-secondary)]">Loading...</div>
            ) : filteredCreators.length === 0 ? (
              <div className="p-4 text-center text-[var(--text-secondary)]">
                {searchTerm ? 'No creators found' : 'No message threads yet'}
              </div>
            ) : (
              filteredCreators.map(creator => (
                <button
                  key={creator.id}
                  onClick={() => setSelectedCreator(creator)}
                  className={`w-full p-4 flex items-start gap-3 border-b border-[var(--border)] hover:bg-[var(--background)] transition-colors text-left ${
                    selectedCreator?.id === creator.id ? 'bg-[var(--background)]' : ''
                  }`}
                >
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[var(--primary)] to-[var(--secondary)] flex items-center justify-center text-white font-bold flex-shrink-0">
                    {creator.username?.[0]?.toUpperCase() || 'C'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="font-medium truncate">{creator.username}</p>
                      {creator.unread_count > 0 && (
                        <span className="px-2 py-0.5 bg-[var(--primary)] text-white text-xs rounded-full">
                          {creator.unread_count}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-[var(--text-secondary)] truncate">
                      {creator.last_message || 'No messages yet'}
                    </p>
                    {creator.last_message_at && (
                      <p className="text-xs text-[var(--text-secondary)] mt-1 flex items-center gap-1">
                        <Clock size={10} />
                        {new Date(creator.last_message_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Message Thread */}
        <div className="flex-1 bg-[var(--surface)] border border-[var(--border)] rounded-xl overflow-hidden flex flex-col">
          {selectedCreator ? (
            <>
              {/* Header */}
              <div className="p-4 border-b border-[var(--border)] flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[var(--primary)] to-[var(--secondary)] flex items-center justify-center text-white font-bold">
                  {selectedCreator.username?.[0]?.toUpperCase() || 'C'}
                </div>
                <div>
                  <p className="font-medium">{selectedCreator.username}</p>
                  <p className="text-xs text-[var(--text-secondary)]">{selectedCreator.email}</p>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.length === 0 ? (
                  <div className="text-center text-[var(--text-secondary)] py-12">
                    <User size={40} className="mx-auto mb-2 opacity-50" />
                    <p>No messages yet</p>
                    <p className="text-sm">Start a conversation with this creator</p>
                  </div>
                ) : (
                  messages.map((msg, idx) => (
                    <div
                      key={msg.id || idx}
                      className={`flex ${msg.from_admin ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-[70%] p-3 rounded-xl ${
                        msg.from_admin 
                          ? 'bg-[var(--primary)] text-white' 
                          : 'bg-[var(--background)] border border-[var(--border)]'
                      }`}>
                        <p className="text-sm">{msg.content}</p>
                        <div className={`flex items-center gap-1 mt-1 text-xs ${
                          msg.from_admin ? 'text-white/70' : 'text-[var(--text-secondary)]'
                        }`}>
                          <span>{new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                          {msg.from_admin && msg.read && (
                            <CheckCheck size={12} />
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <form onSubmit={sendMessage} className="p-4 border-t border-[var(--border)]">
                <div className="flex gap-2">
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type your message..."
                    className="flex-1"
                    disabled={sending}
                  />
                  <Button type="submit" disabled={sending || !newMessage.trim()}>
                    <Send size={18} />
                  </Button>
                </div>
              </form>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-[var(--text-secondary)]">
              <div className="text-center">
                <User size={48} className="mx-auto mb-4 opacity-50" />
                <p>Select a creator to view messages</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminCreatorMessages;
